import { Suspense, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicNav, type NavSurface } from '../components/layout/PublicNav';
import { Footer } from '../components/Footer';
import { ChatButton } from '../components/ChatButton';
import { RouteErrorBoundary } from '../components/ErrorBoundary';
import { PageLoader } from '../components/SuspenseFallback';
import { SkipToContent } from '../utils/accessibility';

/** Routes that render their own fixed bottom bar on mobile (below md) — the
 * bar always covers the same band of the viewport, so whatever the document
 * ends on (the footer) needs that much extra clearance or its last row is
 * permanently unreachable by scrolling. */
const ROUTES_WITH_FIXED_MOBILE_BAR = ['/jasa/belanja'];

/**
 * Shell for every route a visitor can reach without an account.
 *
 * `children` is the escape hatch for the one caller that is not a route:
 * `RequireAdmin` has to render the catch-all 404 inside this very shell, or a
 * non-admin could tell `/admin` (real, gated) apart from `/admin/xyz` (unmapped)
 * by the chrome alone — the disclosure CONTRACT.md §2.4 exists to prevent.
 */
export function PublicLayout({
  surface = 'accent',
  children,
}: {
  surface?: NavSurface;
  children?: ReactNode;
}) {
  const { pathname } = useLocation();
  const hasFixedMobileBar = ROUTES_WITH_FIXED_MOBILE_BAR.includes(pathname);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SkipToContent />
      <PublicNav surface={surface} />
      <main id="main-content" className="flex-1">
        <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            {children ?? <Outlet />}
          </Suspense>
        </RouteErrorBoundary>
      </main>
      <Footer />
      {hasFixedMobileBar && <div className="h-[92px] md:hidden" aria-hidden="true" />}
      <ChatButton />
    </div>
  );
}

export default PublicLayout;
